import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Loading } from "@/components/pawkt/Empty";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageSquare, RotateCcw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const canManage = (u) => u?.role === "owner" || u?.role === "admin" || (u?.effective_permissions || u?.permissions || []).includes("communications.manage") || (u?.permissions || []).includes("*");

export default function CommTemplates() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [sel, setSel] = useState(null);
  const [draft, setDraft] = useState({ subject: "", body: "" });
  const manage = canManage(user);

  const { data, isLoading } = useQuery({ queryKey: ["comm-templates"], queryFn: async () => (await api.get("/communications/templates")).data });

  const pick = (item) => {
    setSel(item.event);
    setDraft({ subject: item.custom?.subject || item.default_subject, body: item.custom?.body || item.default_body });
  };
  const save = useMutation({
    mutationFn: () => api.put("/communications/templates", { event: sel, channel: "", subject: draft.subject, body: draft.body }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["comm-templates"] }); toast.success("Template saved"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const reset = useMutation({
    mutationFn: () => api.delete(`/communications/templates/${sel}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["comm-templates"] }); toast.success("Reverted to default"); setSel(null); },
  });

  if (isLoading) return <Loading />;
  const insertVar = (v) => setDraft((d) => ({ ...d, body: `${d.body}{{${v}}}` }));

  return (
    <div data-testid="comm-templates-page">
      <PageHeader title="Message Templates" subtitle="Customize what customers receive — subject to supported variables & channel rules" />
      {!manage && <div className="mb-4 flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-700"><AlertTriangle className="h-4 w-4" /> Viewing only — needs communications.manage to edit.</div>}
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-1">
          {data.items.map((it) => (
            <button key={it.event} data-testid={`template-item-${it.event}`} onClick={() => pick(it)}
              className={`w-full rounded-lg px-3 py-2 text-left text-sm ${sel === it.event ? "bg-emerald-50 border border-emerald-300" : "hover:bg-slate-50 border border-transparent"}`}>
              <div className="font-medium text-slate-800 capitalize">{it.event.replace(/_/g, " ")}</div>
              <div className="text-xs text-slate-400">{it.channels.join(", ")} {it.customized && <span className="text-emerald-600">· customized</span>}</div>
            </button>
          ))}
        </div>

        <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white p-5">
          {!sel ? <div className="grid h-64 place-items-center text-slate-400"><div className="text-center"><MessageSquare className="mx-auto h-8 w-8 mb-2" /> Select a template to edit</div></div> : (
            <div className="space-y-3">
              <div>
                <label className="text-xs text-slate-500">Subject (email)</label>
                <Input value={draft.subject} onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))} disabled={!manage} data-testid="template-subject" className="mt-1 bg-white" />
              </div>
              <div>
                <label className="text-xs text-slate-500">Message body</label>
                <textarea value={draft.body} onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))} disabled={!manage} data-testid="template-body" rows={5} className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm" />
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1">Insert variable</div>
                <div className="flex flex-wrap gap-1.5">
                  {(data.variables || []).map((v) => (
                    <button key={v} disabled={!manage} data-testid={`var-${v}`} onClick={() => insertVar(v)} className="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-600 hover:bg-slate-200 disabled:opacity-50">{`{{${v}}}`}</button>
                  ))}
                </div>
                <p className="mt-2 text-xs text-slate-400">Only these variables are allowed. Unknown variables are rejected on save.</p>
              </div>
              <div className="flex gap-2 pt-1">
                <Button onClick={() => save.mutate()} disabled={!manage || save.isPending} data-testid="template-save" className="bg-slate-900 text-white">Save template</Button>
                <Button variant="outline" onClick={() => reset.mutate()} disabled={!manage} data-testid="template-reset" className="gap-1.5"><RotateCcw className="h-4 w-4" /> Reset to default</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
